import React from 'react';
import { useTranslation } from 'react-i18next';
import { Skeleton } from 'primereact/skeleton';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

import { useLoyaltyAnalytics } from '../../queries/loyalty';
import LoyaltyNav from './LoyaltyNav';
import '../../styles/honey-ink.css';
import { useInHub } from '../../nav/useInHub';

/**
 * QR Loyalty & Ordering — Phase 5. The owner Analytics dashboard.
 *
 * Read-only roll-up from GET /api/loyalty/analytics: four stat cards (with ↑↓%
 * vs last month), a best-performing featured item, a weekly check-ins/orders bar
 * chart (Recharts), auto-generated smart tips, and a recent-activity feed. Honey
 * & Ink styled, bilingual (EN/AR), fully responsive. Smart tips arrive as
 * {key, params} and are localized here so both languages read naturally. See
 * .claude/QR_LOYALTY_DESIGN.md §7C.
 */
export default function LoyaltyAnalytics() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useLoyaltyAnalytics();
  const lng = i18n.language?.split('-')[0] || 'en';
  const isAr = lng === 'ar';

  const daysShort = t('loyalty.analytics.days_short', { returnObjects: true }) || [];
  const days = t('loyalty.analytics.days', { returnObjects: true }) || [];

  const cards = [
    { key: 'new_customers_count', label: t('loyalty.analytics.card_new_customers'), icon: '🧑‍🤝‍🧑' },
    { key: 'checkins_count', label: t('loyalty.analytics.card_checkins'), icon: '🚪' },
    { key: 'orders_count', label: t('loyalty.analytics.card_orders'), icon: '🧾' },
  ];

  if (isLoading || !data) {
    return (
      <div className="lty-app lty-page p-4 md:p-6">
        <LoyaltyNav />
        <Header t={t} />
        <div className="lty-stat-grid">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} height="118px" borderRadius="24px" />)}
        </div>
        <Skeleton height="120px" borderRadius="24px" className="mt-4" />
        <Skeleton height="320px" borderRadius="24px" className="mt-4" />
      </div>
    );
  }

  const chartData = (data.daily_activity || []).map((d) => ({
    label: daysShort[d.dow] ?? d.date?.slice(5),
    [t('loyalty.analytics.legend_checkins')]: d.checkins,
    [t('loyalty.analytics.legend_orders')]: d.orders,
  }));
  const hasChart = chartData.some((d) => Object.values(d).some((v) => typeof v === 'number' && v > 0));

  return (
    <div className="lty-app lty-page p-4 md:p-6">
      <LoyaltyNav />
      <Header t={t} />

      {/* 1 — stat cards */}
      <div className="lty-stat-grid">
        {cards.map((c) => (
          <StatCard key={c.key} icon={c.icon} label={c.label}
            current={data[c.key]?.current ?? 0} previous={data[c.key]?.previous ?? 0} t={t} />
        ))}
        <div className="lty-stat-card">
          <div className="lty-stat-top">
            <span className="lty-stat-ico">🎁</span>
            <span className="lty-stat-label">{t('loyalty.analytics.card_points')}</span>
          </div>
          <div className="lty-stat-value">{fmt(data.points_distributed ?? 0, lng)}</div>
          <div className="lty-stat-delta flat">{t('loyalty.analytics.vs_last_month')}</div>
        </div>
      </div>

      {/* 2 — best performer */}
      <div className="lty-best">
        <div className="lty-best-ico">🏆</div>
        <div className="lty-best-body">
          <div className="lty-best-kicker">{t('loyalty.analytics.best_performer')}</div>
          {data.top_item ? (
            <>
              <div className="lty-best-name">
                {isAr && data.top_item.name_ar ? data.top_item.name_ar : data.top_item.name}
              </div>
              <div className="lty-best-sub">{t('loyalty.analytics.best_performer_sub')}</div>
            </>
          ) : (
            <div className="lty-best-sub">{t('loyalty.analytics.no_best')}</div>
          )}
        </div>
        {data.top_item && (
          <div className="lty-best-count">
            <span className="n">{fmt(data.top_item.order_count, lng)}</span>
            <span className="l">{t('loyalty.analytics.card_orders')}</span>
          </div>
        )}
      </div>

      {/* 3 — weekly activity chart */}
      <div className="lty-an-card">
        <div className="lty-card-title">{t('loyalty.analytics.chart_title')}</div>
        <div className="lty-card-sub">{t('loyalty.analytics.chart_sub')}</div>
        {hasChart ? (
          <div className="lty-chart" dir="ltr">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, left: -18, bottom: 0 }} barGap={6}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--lty-line)" vertical={false} />
                <XAxis dataKey="label" tick={{ fill: 'var(--lty-ink-soft)', fontSize: 12 }}
                  axisLine={{ stroke: 'var(--lty-line)' }} tickLine={false} />
                <YAxis allowDecimals={false} tick={{ fill: 'var(--lty-ink-faint)', fontSize: 12 }}
                  axisLine={false} tickLine={false} width={34} />
                <Tooltip cursor={{ fill: 'var(--lty-honey-soft)', opacity: 0.4 }} content={<ChartTip />} />
                <Legend wrapperStyle={{ fontSize: 13, color: 'var(--lty-ink-soft)' }} iconType="circle" />
                <Bar dataKey={t('loyalty.analytics.legend_checkins')} fill="var(--lty-honey)" radius={[6, 6, 0, 0]} maxBarSize={34} />
                <Bar dataKey={t('loyalty.analytics.legend_orders')} fill="var(--lty-jade)" radius={[6, 6, 0, 0]} maxBarSize={34} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="lty-empty">{t('loyalty.analytics.no_chart')}</div>
        )}
      </div>

      {/* 4 — smart tips */}
      <div className="lty-an-card">
        <div className="lty-card-title">{t('loyalty.analytics.tips_title')}</div>
        <div className="lty-card-sub">{t('loyalty.analytics.tips_sub')}</div>
        {data.smart_tips?.length ? (
          <div className="lty-tips">
            {data.smart_tips.map((tip, i) => (
              <div className="lty-tip-card" key={`${tip.key}-${i}`}>{tipText(t, tip, days)}</div>
            ))}
          </div>
        ) : (
          <div className="lty-empty">{t('loyalty.analytics.no_tips')}</div>
        )}
      </div>

      {/* 5 — recent activity */}
      <div className="lty-an-card">
        <div className="lty-card-title">{t('loyalty.analytics.recent_title')}</div>
        <div className="lty-card-sub">{t('loyalty.analytics.recent_sub')}</div>
        {data.recent_activity?.length ? (
          <div className="lty-recent">
            {data.recent_activity.map((ev, i) => (
              <div className="lty-recent-item" key={i}>
                <span className={`lty-recent-ico ${ev.type}`}>{EV_ICON[ev.type] || '•'}</span>
                <span className="lty-recent-text">{eventText(t, ev, isAr)}</span>
                <span className="lty-recent-time">{timeAgo(ev.timestamp, lng)}</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="lty-empty">{t('loyalty.analytics.no_recent')}</div>
        )}
      </div>
    </div>
  );
}

/* ── pieces ─────────────────────────────────────────────────────────────────── */

function Header({ t }) {
  const inHub = useInHub();
  if (inHub) return null;
  return (
    <div className="lty-head">
      <div className="lty-head-icon"><i className="pi pi-chart-bar" /></div>
      <div>
        <div className="lty-title">{t('loyalty.analytics.title')}</div>
        <div className="lty-sub">{t('loyalty.analytics.sub')}</div>
      </div>
    </div>
  );
}

function StatCard({ icon, label, current, previous, t }) {
  const { pct, dir } = delta(current, previous);
  return (
    <div className="lty-stat-card">
      <div className="lty-stat-top">
        <span className="lty-stat-ico">{icon}</span>
        <span className="lty-stat-label">{label}</span>
      </div>
      <div className="lty-stat-value">{current}</div>
      <div className={`lty-stat-delta ${dir}`}>
        {dir !== 'flat' && <i className={`pi ${dir === 'up' ? 'pi-arrow-up' : 'pi-arrow-down'}`} />}
        {pct != null ? <b>{pct > 0 ? '+' : ''}{pct}%</b> : null}
        <span className="lbl">{t('loyalty.analytics.vs_last_month')}</span>
      </div>
    </div>
  );
}

function ChartTip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="lty-chart-tip">
      <div className="ttl">{label}</div>
      {payload.map((p) => (
        <div className="row" key={p.dataKey}>
          <span className="dot" style={{ background: p.color }} />
          <span className="k">{p.name}</span>
          <span className="v">{p.value}</span>
        </div>
      ))}
    </div>
  );
}

const EV_ICON = { checkin: '🚪', order: '🧾', redemption: '🎁' };

/* ── helpers ────────────────────────────────────────────────────────────────── */

// Percent change vs the previous period. Null pct when there's no base to compare.
function delta(current, previous) {
  if (!previous) {
    if (current > 0) return { pct: null, dir: 'up' };
    return { pct: null, dir: 'flat' };
  }
  const pct = Math.round(((current - previous) / previous) * 100);
  return { pct, dir: pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat' };
}

function fmt(n, lng) {
  try { return new Intl.NumberFormat(lng === 'ar' ? 'ar-EG' : 'en-US').format(n); }
  catch { return String(n); }
}

function tipText(t, tip, days) {
  const params = { ...(tip.params || {}) };
  if (tip.key === 'quietest_day') params.day = days?.[params.dow] ?? '';
  return t(`loyalty.analytics.tip.${tip.key}`, params);
}

function eventText(t, ev, isAr) {
  const name = ev.customer_name || t('loyalty.analytics.a_guest');
  if (ev.type === 'order') {
    const item = isAr && ev.item_name_ar ? ev.item_name_ar : ev.item_name;
    return item
      ? t('loyalty.analytics.ev_order', { name, item })
      : t('loyalty.analytics.ev_order_no_item', { name });
  }
  if (ev.type === 'redemption') return t('loyalty.analytics.ev_redemption', { name, points: ev.points ?? 0 });
  return t('loyalty.analytics.ev_checkin', { name });
}

function timeAgo(iso, lng) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Math.round((then - Date.now()) / 1000); // negative = past
  const abs = Math.abs(diff);
  try {
    const rtf = new Intl.RelativeTimeFormat(lng === 'ar' ? 'ar' : 'en', { numeric: 'auto' });
    if (abs < 60) return rtf.format(Math.round(diff), 'second');
    if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
    if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
    if (abs < 604800) return rtf.format(Math.round(diff / 86400), 'day');
    return rtf.format(Math.round(diff / 604800), 'week');
  } catch {
    return new Date(iso).toLocaleDateString();
  }
}
